/**
 * The key-creation form: a name, the scope picker, and the one call that returns the
 * full key. The parent shows the key afterwards - it is displayed once and never again,
 * so it must survive this form being unmounted.
 */
import { useState } from "react";
import { Button, Input, Label } from "@wa/ui";
import type { ApiScopeGroup } from "../../../lib/apiKeys";
import { apiKeysApi } from "../../../lib/apiKeys";
import { ScopePicker } from "./ScopePicker";

interface Props {
  t: (key: string) => string;
  groups: ApiScopeGroup[];
  onCreated: (fullKey: string) => void;
}

export function CreateKeyForm({ t, groups, onCreated }: Props) {
  const [name, setName] = useState("");
  const [selected, setSelected] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setError(null);
    if (!name.trim() || selected.length === 0) {
      setError(t("apiKeys.needNameScope"));
      return;
    }
    setBusy(true);
    try {
      const r = await apiKeysApi.create(name.trim(), selected);
      onCreated(r.fullKey);
    } catch {
      setError(t("apiKeys.createFailed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-3 rounded-md border p-3">
      <div className="space-y-1.5">
        <Label className="text-xs">{t("apiKeys.name")}</Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Zapier" />
      </div>

      <div className="space-y-1.5">
        <div className="flex items-center justify-between gap-2">
          <Label className="text-xs">{t("apiKeys.scopes")}</Label>
          <span className="text-xs text-muted-foreground">
            {t("apiKeys.selectedCount").replace("{n}", String(selected.length))}
          </span>
        </div>
        <p className="text-xs text-muted-foreground">{t("apiKeys.scopesHint")}</p>
        <ScopePicker groups={groups} selected={selected} onChange={setSelected} t={t} />
      </div>

      {error && <p className="text-xs text-destructive">{error}</p>}

      <div className="flex justify-end">
        <Button size="sm" onClick={() => void submit()} disabled={busy}>
          {t("apiKeys.createBtn")}
        </Button>
      </div>
    </div>
  );
}
